export interface CookieOptions {
  secure?: boolean
  httpOnly?: boolean
  sameSite?: 'strict' | 'lax' | 'none'
  maxAge?: number
  path?: string
}

const defaultCookieOptions: CookieOptions = {
  secure: true,
  httpOnly: false,
  sameSite: 'strict',
  path: '/',
}

// HTTPOnly cookies need to be set from UM-BE - this function ensures we are setting tokens with 'secure' flag and correctly clearing log in/out state

export function setSecureCookie(name: string, value: string, options: CookieOptions = {}): void {
  const cookieOptions = { ...defaultCookieOptions, ...options }

  let cookieString = `${encodeURIComponent(name)}=${encodeURIComponent(value)}`

  if (cookieOptions.secure) {
    cookieString += '; Secure'
  }

  if (cookieOptions.httpOnly) {
    cookieString += '; HttpOnly'
  }

  if (cookieOptions.sameSite) {
    cookieString += `; SameSite=${cookieOptions.sameSite}`
  }

  if (cookieOptions.maxAge) {
    cookieString += `; Max-Age=${cookieOptions.maxAge}`
  }

  if (cookieOptions.path) {
    cookieString += `; Path=${cookieOptions.path}`
  }

  document.cookie = cookieString
}

export function getCookie(name: string): string | null {
  const value = `; ${document.cookie}`
  const parts = value.split(`; ${encodeURIComponent(name)}=`)
  if (parts.length === 2) {
    const cookieValue = parts.pop()?.split(';').shift()
    return cookieValue ? decodeURIComponent(cookieValue) : null
  }
  return null
}

export function removeCookie(name: string, path: string = '/'): void {
  document.cookie = `${encodeURIComponent(
    name
  )}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=${path}; Secure; SameSite=strict`
}

export function setCSRFToken(token: string): void {
  setSecureCookie('XSRF-TOKEN', token)
}

export function getCSRFToken(): string | null {
  // Prioritize the new cookie name
  const xsrfToken = getCookie('XSRF-TOKEN')
  if (xsrfToken) return xsrfToken

  // Fallback to legacy cookie name
  return getCookie('csrfToken')
}

export function removeCSRFToken(): void {
  removeCookie('XSRF-TOKEN')
  removeCookie('csrfToken')
}

export function setOIDCAccessToken(token: string): void {
  setSecureCookie('oidcAccessToken', token)
}

export function getOIDCAccessToken(): string | null {
  return getCookie('oidcAccessToken')
}

export function removeOIDCAccessToken(): void {
  removeCookie('oidcAccessToken')
}

export function setOIDCRefreshToken(token: string): void {
  setSecureCookie('oidcRefreshToken', token)
}

export function getOIDCRefreshToken(): string | null {
  return getCookie('oidcRefreshToken')
}

export function removeOIDCRefreshToken(): void {
  removeCookie('oidcRefreshToken')
}

export function clearAuthTokens(): void {
  removeCSRFToken()
  removeOIDCAccessToken()
  removeOIDCRefreshToken()
}
